import React from 'react';
import { Calendar, Building, AlertTriangle, Settings, DollarSign, MapPin, FileText } from 'lucide-react';
import { SelectedContract, LLMMode, AIProvider } from '../types';
import { LLM_MODE_CONFIG } from '../../../../services/legalAIService';

interface MetadataPanelProps {
  contract: SelectedContract | null;
  mode: LLMMode;
  provider: AIProvider | undefined;
  onModeChange: (mode: LLMMode) => void;
  onProviderChange: (provider: AIProvider | undefined) => void;
}

const STATUS_COLORS: Record<string, string> = {
  'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  'pending_review': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'under_review': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'expired': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'terminated': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
};

export const MetadataPanel: React.FC<MetadataPanelProps> = ({
  contract,
  mode,
  provider,
  onModeChange,
  onProviderChange
}) => {
  if (!contract) {
    return (
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 dark:border-dark-border">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contract Details</h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No contract selected</p>
            <p className="text-sm mt-1">Select a contract from the list to view details</p>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number | undefined) => {
    if (!value) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="p-4 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Contract Details</h2>
      </div>

      <div className="p-4 space-y-6">
        {/* Contract Details */}
        <section>
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Overview
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Title</p>
              <p className="font-medium text-gray-900 dark:text-white text-sm">{contract.title}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Type</p>
              <p className="font-medium text-gray-900 dark:text-white text-sm capitalize">{contract.type}</p>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">Supplier</p>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{contract.supplierName}</p>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
              <span className={`inline-block px-2 py-0.5 text-xs rounded-full capitalize mt-1 ${
                STATUS_COLORS[contract.status] || STATUS_COLORS['draft']
              }`}>
                {contract.status.replace('_', ' ')}
              </span>
            </div>
            {contract.value && (
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Value</p>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">
                    {formatCurrency(contract.value)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Key Dates */}
        <section>
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Key Dates
          </h3>
          <div className="space-y-2 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">Start Date</span>
              <span className="text-gray-900 dark:text-white font-medium">{contract.startDate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">End Date</span>
              <span className="text-gray-900 dark:text-white font-medium">{contract.endDate}</span>
            </div>
          </div>
        </section>

        {/* Jurisdiction */}
        {contract.jurisdiction && (
          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Jurisdiction
            </h3>
            <p className="text-sm text-gray-900 dark:text-white">{contract.jurisdiction}</p>
          </section>
        )}

        {/* Risk Flags */}
        {contract.riskFlags && contract.riskFlags.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Risk Flags ({contract.riskFlags.length})
            </h3>
            <div className="space-y-2">
              {contract.riskFlags.map((flag, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-sm text-amber-800 dark:text-amber-300"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{flag}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* AI Settings */}
        <section>
          <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4" />
            AI Settings
          </h3>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">AI Provider</label>
              <select
                value={provider || ''}
                onChange={(e) => onProviderChange(e.target.value as AIProvider || undefined)}
                className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Auto (Default)</option>
                <option value="anthropic">Claude (Anthropic)</option>
                <option value="openai">GPT (OpenAI)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Current Mode</label>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {LLM_MODE_CONFIG[mode].label}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {LLM_MODE_CONFIG[mode].description}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default MetadataPanel;
